import { useTranslation } from 'react-i18next';
import { Check, Mail, MessageCircle, MessageSquare } from 'lucide-react';
import classnames from 'classnames';
import { OTP_DELIVERY_CHANNELS } from '../../services/exports/Constants';
import useOtpChannels from '../../hooks/auth/useOtpChannels';

interface Props {
  value: OTP_DELIVERY_CHANNELS;
  onChange: (channel: OTP_DELIVERY_CHANNELS) => void;
  disabled?: boolean;
  className?: string;
}

const OPTIONS = [
  {
    channel: OTP_DELIVERY_CHANNELS.WHATSAPP,
    Icon: MessageCircle,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  { channel: OTP_DELIVERY_CHANNELS.SMS, Icon: MessageSquare, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  { channel: OTP_DELIVERY_CHANNELS.EMAIL, Icon: Mail, iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
] as const;

export default function ChannelChooser(props: Props) {
  const { value, onChange, disabled, className } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Components:Auth:OtpFlow:channel_chooser' });
  const { channels } = useOtpChannels();

  const options = OPTIONS.filter(({ channel }) => channels.includes(channel));

  // Nothing to choose: an email-only partner offers a single channel, so the chooser
  // would be a one-option radio group. The flow pre-selects it instead.
  if (options.length < 2) {
    return null;
  }

  return (
    <div className={className}>
      <p className={titleStyle}>{t('title')}</p>
      <div className={gridStyle} role="radiogroup" aria-label={t('title')}>
        {options.map(({ channel, Icon, iconBg, iconColor }) => {
          const selected = value === channel;

          return (
            <button
              key={channel}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(channel)}
              disabled={disabled}
              className={classnames(cardBase, selected ? cardSelected : cardUnselected, {
                'cursor-not-allowed opacity-60': disabled,
              })}
              data-test={`otp-channel-${channel}`}
            >
              <div className={classnames(iconWrapper, iconBg)}>
                <Icon className={classnames('h-5 w-5', iconColor)} />
              </div>
              <div className={textWrapper}>
                <p className={labelStyle}>{t(`${channel}.label`)}</p>
                <p className={subtitleStyle}>{t(`${channel}.subtitle`)}</p>
              </div>
              <div
                className={classnames(
                  selectionDot,
                  selected ? 'bg-brand-primary text-white' : 'border-[1px] border-brand-border bg-transparent',
                )}
              >
                {selected && <Check className="h-3 w-3" strokeWidth={3} />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const titleStyle = 'text-sm font-medium text-brand-text-primary mb-xs';
// Always two columns: availableOtpDeliveryChannels() returns either ['sms','whatsapp'] or
// ['email'] alone, and a single option renders nothing at all (see above).
const gridStyle = 'grid grid-cols-2 gap-xs';
const cardBase =
  'flex items-center gap-xs p-sm rounded-lg border-[1px] text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30';
const cardSelected = 'border-brand-primary bg-brand-primary/5';
const cardUnselected = 'border-brand-border bg-card hover:border-brand-primary/40';
const iconWrapper = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full';
const textWrapper = 'flex-1 min-w-0';
const labelStyle = 'text-sm font-semibold text-brand-text-primary leading-tight';
const subtitleStyle = 'text-xs text-muted-foreground leading-tight';
const selectionDot = 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full';
